import { Injectable } from '@angular/core';
import {
	Filter,
	Location,
	StatusDisplayItem,
	MetaDataValue,
	StatusMetaData,
	ReferenceValue,
} from 'company-finder-common';
import { MenuOption, GroupedMenuOptions } from './menu-option.interface.js';
import { DeploymentContext } from '../../utilities/deployment-context/deployment-context.js';

// By design, this service is not a singleton so every consuming component must register
// it as a provider
@Injectable({ providedIn: 'root' })
export class MenuOptionService {
	public locationOptions: MenuOption<Location>[] = [];
	public indicationOptions: MenuOption<string>[] = [];
	public statusOptions: GroupedMenuOptions<StatusDisplayItem>[] = [];
	public therapeuticAreaOptions: MenuOption<string>[] = [];

	constructor(private deploymentContext: DeploymentContext) {}

	public async initMenus(filter: Filter): Promise<void> {
		const summary = this.deploymentContext.comprehensiveSummary;

		this.locationOptions = summary?.locations
			.map((location) => {
				let value = false;
				if (filter?.locations?.length !== summary.locations.length) {
					value = !!filter?.locations.find(
						(filterLocation) => filterLocation === location.name
					);
				}
				return {
					id: location.name,
					label: location.name,
					value: value,
					dataModel: location,
				};
			})
			.sort((a, b) => {
				return a.dataModel.sortOrder - b.dataModel.sortOrder;
			});

		this.statusOptions = this.usedStatusGroups.map((meta) =>
			this.metaToMenuOption(meta, filter)
		);

		this.indicationOptions = this.refDataToMenuOptions(
			this.deploymentContext.referenceValueData?.indications,
			filter?.indications ?? []
		);
		this.therapeuticAreaOptions = this.refDataToMenuOptions(
			this.deploymentContext.referenceValueData?.therapeuticAreas,
			filter?.therapeuticAreas ?? []
		);
	}

	public static stringsToMenuOptions(
		options: string[],
		filterOptions: string[]
	) {
		return (
			options.map((option) => ({
				id: option,
				label: option,
				value: !!filterOptions?.find(
					(filterOption) => filterOption === option
				),
				dataModel: option,
			})) ?? []
		);
	}

	public refDataToMenuOptions(
		options: ReferenceValue[],
		filterOptions: string[]
	) {
		return options.map((option) => ({
			id: option.value,
			label: option.value,
			value: !!filterOptions?.find(
				(filterIndication) => filterIndication === option.value
			),
			dataModel: option.value,
		}));
	}

	private metaToMenuOption(
		meta: StatusMetaData,
		filter: Filter
	): GroupedMenuOptions<StatusDisplayItem> {
		return {
			groupName: meta.groupName,
			category: meta.statusType,
			options: meta.values
				.filter((metaValue) => this.isStatusUsed(metaValue.value))
				.map((metaValue) =>
					this.metaValueToMenuOption(metaValue, filter)
				),
		};
	}

	private metaValueToMenuOption(
		value: MetaDataValue,
		filter: Filter
	): MenuOption<StatusDisplayItem> {
		return {
			id: value.value,
			label: value.display,
			value:
				filter?.locationStatuses.includes(value.value) ||
				filter?.companyStatuses?.includes(value.value),
			dataModel: {
				status: value.value,
				displayName: value.display,
			},
		};
	}

	private _usedStatusGroups: StatusMetaData[];

	public get usedStatusGroups(): StatusMetaData[] {
		if (!this._usedStatusGroups) {
			this._usedStatusGroups =
				this.deploymentContext.statusMetadata.filter((meta) =>
					meta.values.some((metaValue) =>
						this.isStatusUsed(metaValue.value)
					)
				);
		}
		return this._usedStatusGroups;
	}

	private isStatusUsed(status: string): boolean {
		return this.deploymentContext.comprehensiveSummary?.allStatusesWithCounts.some(
			(statusWithCount) =>
				statusWithCount.statusItem.status === status &&
				statusWithCount.count > 0
		);
	}
}
